import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { isInvalid } from '../../../../shared/helpers/validate';
import { UpsertContactPersonDialogStore } from './upsert-contact-person-dialog.store';
import { dateify } from '../../../../shared/helpers/date.helper';
import { contentGlobalSuccess } from '../../../content.error';
import {
    OrganizationCenterPersonObject,
} from '@libs/types/src/organization/object-type/organization-center-person.object';
import { BaseDialogComponent } from '../../../../../components/kit/base-dialog/base-dialog.component';
import { AppFormGroup } from '../../../../../utils/form.utils';
import { fb } from '../../../../shared/shared.global-service';
import { EditPersonFormComponent } from '../edit-person-form/edit-person-form.component';

@Component({
    selector: 'app-upsert-contact-person-dialog',
    templateUrl: './upsert-contact-person-dialog.component.html',
    styleUrls: ['upsert-contact-person-dialog.component.scss'],
    providers: [UpsertContactPersonDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ReactiveFormsModule,
        EditPersonFormComponent,
    ],
})
export class UpsertContactPersonDialogComponent extends BaseDialogComponent {

    public readonly store = inject(UpsertContactPersonDialogStore);

    protected form = new AppFormGroup({
        person: fb.group({
            name: fb.control<string | null>(null),
            surname: fb.control<string | null>(null, Validators.required),
            titleBeforeName: fb.control<string | null>(null),
            titleAfterName: fb.control<string | null>(null),
            password: fb.control<string | null>(null, Validators.required),
            email: fb.control<string | null>(null, [Validators.required, Validators.email]),
            note: fb.control<string | null>(null),
            dateFrom: fb.control<Date>(new Date(), [Validators.required]),
            sex: fb.control<string | null>('male'),
            chosenPersonId: fb.control<number | null>(null),
            groupIds: fb.control<number[]>([]),
        }),
    });

    private createContact: boolean = false;
    private createRepresentative: boolean = false;
    private person: OrganizationCenterPersonObject | null = null;
    private centerId: number = 0;

    constructor() {
        super();

        this.createContact = this.dialogConfig.data.createContact || false;
        this.createRepresentative = this.dialogConfig.data.createRepresentative || false;
        this.person = this.dialogConfig.data.person;
        this.centerId = this.dialogConfig.data.centerId;

        this.form.patchValue({
            person: {
                name: this.person?.user?.name || null,
                surname: this.person?.user?.surname || null,
                titleBeforeName: this.person?.user?.titleBeforeName || null,
                titleAfterName: this.person?.user?.titleAfterName || null,
                email: this.person?.user?.email || null,
                password: null,
                note: this.person?.note || null,
                dateFrom: dateify(this.person?.validFrom),
                sex: (this.person?.user as any)?.sex || 'male',
                chosenPersonId: null,
                groupIds: this.person?.user?.groups?.map((it) => it.id) || [],
            },
        });
        if (this.person) {
            this.form.get('person.password')?.disable();
            this.form.get('person.password')?.clearValidators();
        } else {
            this.form.get('person.password')?.enable();
            this.form.get('person.password')?.clearValidators();
            this.form.get('person.password')?.addValidators(Validators.required);
        }
    }

    public override async save() {
        if (isInvalid(this.form)) return;

        if (!this.person) {
            await this.createPerson();
        } else {
            await this.updatePerson();
        }
    }

    private async createPerson(): Promise<void> {
        this.store.api.createOrganizationCenterPerson({
            name: this.form.value.person?.name || '',
            surname: this.form.value.person?.surname || '',
            titleBeforeName: this.form.value.person?.titleBeforeName || '',
            titleAfterName: this.form.value.person?.titleAfterName || '',
            password: this.form.value.person?.password || '',
            email: this.form.value.person?.email || '',
            note: this.form.value.person?.note || '',
            validFrom: this.form.value.person?.dateFrom?.toISOString() || '',
            sex: this.form.value.person?.sex || 'male',
            centerId: this.centerId,
            contact: this.createContact,
            representative: this.createRepresentative,
            groupIds: this.form.value.person?.groupIds || [],
        }).then(() => {
            contentGlobalSuccess.next('common.created');
            this.dialogRef.close(true);
        });
    }

    private async updatePerson(): Promise<void> {
        this.store.api.updateOrganizationCenterPerson({
            id: this.person!.id,
            name: this.form.value.person?.name || '',
            surname: this.form.value.person?.surname || '',
            titleBeforeName: this.form.value.person?.titleBeforeName || '',
            titleAfterName: this.form.value.person?.titleAfterName || '',
            email: this.form.value.person?.email || '',
            note: this.form.value.person?.note || '',
            validFrom: this.form.value.person?.dateFrom?.toISOString() || '',
            sex: this.form.value.person?.sex || 'male',
            groupIds: this.form.value.person?.groupIds || [],
        }).then(() => {
            contentGlobalSuccess.next('common.updated');
            this.dialogRef.close(true);
        });
    }
}
